import { type FormEvent, useState } from 'react';
import { i18n, type PalletState } from '@wms/shared';
import { Dialog } from './Dialog.js';

const { t } = i18n;

/**
 * Confirm a pallet state change with a mandatory reason field (2026-05-28
 * customer feedback). Replaces window.prompt. Hint text adapts to the target
 * state so operators get a nudge about what to write.
 */
export function TransitionDialog({
  toState,
  busy,
  onClose,
  onConfirm,
}: {
  toState: PalletState;
  busy: boolean;
  onClose: () => void;
  onConfirm: (reason: string, date: string) => void | Promise<void>;
}) {
  const [reason, setReason] = useState('');
  // Effective date of the operation — defaults to today, overridable (WMS-16).
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed) {
      setError(t('pallet.transition.reason_required'));
      return;
    }
    setError(null);
    await onConfirm(trimmed, date);
  }

  const hintKey = `pallet.transition.hint.${toState}`;
  const hint = t(hintKey);
  const stateLabel = t(`pallet.state.${toState}`);

  return (
    <Dialog
      title={`${t('pallet.transition.title')} → ${stateLabel}`}
      onClose={onClose}
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3 text-sm">
        <label className="block space-y-1">
          <span className="font-medium">{t('pallet.transition.reason_label')}</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            required
            autoFocus
            placeholder={hint === hintKey ? undefined : hint}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
          {hint !== hintKey ? (
            <span className="block text-xs text-muted-foreground">{hint}</span>
          ) : null}
        </label>

        <label className="block space-y-1">
          <span className="font-medium">{t('pallet.op_date.label')}</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>

        {error ? <div className="text-red-600">{error}</div> : null}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('pallet.transition.confirm')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
